import React, { useState } from 'react';
import apiService from '../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';

interface AddCandidateModalProps {
  open: boolean;
  onClose: () => void;
  onCandidateAdded: () => void;
}

const AddCandidateModal: React.FC<AddCandidateModalProps> = ({
  open,
  onClose,
  onCandidateAdded,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    currentTitle: '',
    currentCompany: '',
    linkedInUrl: '',
    source: 'referral',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const candidateData = {
        ...formData,
        fullName: `${formData.firstName} ${formData.lastName}`,
      };

      await apiService.createCandidate(candidateData);

      onCandidateAdded();
      onClose();
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        currentTitle: '',
        currentCompany: '',
        linkedInUrl: '',
        source: 'referral',
        notes: '',
      });
    } catch (err: any) {
      console.error('Error adding candidate:', err);
      setError(err.message || 'Failed to add candidate');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.firstName && formData.lastName && formData.email;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Candidate</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              required
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Current Title"
              value={formData.currentTitle}
              onChange={(e) => handleInputChange('currentTitle', e.target.value)}
              placeholder="e.g., Software Engineer"
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Current Company"
              value={formData.currentCompany}
              onChange={(e) => handleInputChange('currentCompany', e.target.value)}
              placeholder="e.g., Google"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="LinkedIn URL"
              value={formData.linkedInUrl}
              onChange={(e) => handleInputChange('linkedInUrl', e.target.value)}
              placeholder="https://linkedin.com/in/..."
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              options={[
                { value: 'referral', label: 'Referral' },
                { value: 'job_board', label: 'Job Board' },
                { value: 'linkedin', label: 'LinkedIn' },
                { value: 'company_website', label: 'Company Website' },
                { value: 'recruiter', label: 'Recruiter' },
                { value: 'career_fair', label: 'Career Fair' },
                { value: 'other', label: 'Other' },
              ]}
              getOptionLabel={(option) => option.label}
              value={{ value: formData.source, label: formData.source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
              onChange={(event, newValue) => {
                handleInputChange('source', newValue?.value || 'referral');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Source"
                  helperText="How did you find this candidate?"
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes about the candidate..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid}
        >
          {loading ? <CircularProgress size={20} /> : 'Add Candidate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCandidateModal;